import {
  type EnrichmentCreditsSummary,
  MAX_SPEC_SEAT_QUANTITY,
  SPEC_CREDIT_PURCHASE_MAX_CENTS,
  SPEC_CREDIT_PURCHASE_MIN_CENTS,
  SPEC_MICROS_PER_CREDIT,
  SPEC_SPEND_LIMIT_MAX_MICROS,
  type SpecBillingSummary,
  type SpecCreditCheckoutResponse,
  type SpecUsageBreakdown,
  type SpecUsageSummary,
} from '@parcel/types';
import { ApiError, apiFetch } from './api.js';

/**
 * Typed client for the Spec AI add-on billing surface: reads the workspace's
 * seat and credit summary and writes the owner or admin seat controls
 * (purchased quantity and assignment set).
 *
 * The wire shape is `SpecBillingSummarySchema` in packages/types/src/spec.ts and
 * the routes are in apps/api/src/routes/spec-usage.ts. Two rules from that file
 * shape everything below:
 *
 *  - Every SEAT write returns the FULL summary. Callers render the response,
 *    never the value they hoped for, so a rejected or partially reconciled
 *    write can never leave a control displaying a number the server refused.
 *    The two credit-settings writes are the exception and echo only the fields
 *    they wrote, so their callers re-read rather than assume the rest.
 *  - Each PATCH body is validated with an EXACT-KEYS check (`hasExactKeys`,
 *    spec-usage.ts:70). One extra or missing key is a 400, so each request body
 *    below is built key by key rather than forwarded from a caller's object.
 */

/** The only `effective` the seat-quantity route accepts (spec-usage.ts:100). */
export const SPEC_SEAT_EFFECTIVE = 'immediate_add_or_period_end_reduce' as const;
export type SpecSeatEffective = typeof SPEC_SEAT_EFFECTIVE;

// Re-exported so the seat control takes its bound from the same place the
// route's own validator does, rather than keeping a second 1000 that could
// silently disagree with it.
export { MAX_SPEC_SEAT_QUANTITY };

const CREDIT_DISPLAY_MAX_FRACTION_DIGITS = 2;

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/**
 * Rejections these routes return that a page has a distinct answer for.
 *
 * Anything else (a 500, a missing workspace) stays a raw `ApiError`: an
 * unhandled error is safer than a mislabeled one.
 *
 * The validation 400s ARE in this list, and deliberately so. The client
 * mirrors each server bound so the dialog can say what is wrong before a
 * request goes out, but the mirrors are not complete: the auto-reload amount
 * guard is skipped while DISABLING, and the threshold ceiling is a
 * per-workspace row the client never reads. Both paths therefore reach a 400
 * the user has to act on, and without a mapping they render as "Unable to save
 * these auto-reload settings" on the one primitive in this PR that spends a
 * card unattended.
 */
const SPEC_BILLING_ERROR_CODES = [
  'billing_forbidden',
  'spec_plan_ineligible',
  'spec_assignment_limit',
  'spec_assignment_stale',
  'spec_member_inactive',
  'spec_payment_required',
  'spec_pricing_unavailable',
  'spec_read_only',
  'spec_not_launched',
  'spec_credit_purchases_disabled',
  'spec_checkout_unavailable',
  'spec_auto_reload_spend_limit_required',
  'invalid_amount',
  'invalid_threshold',
  'invalid_limit',
  'limit_out_of_bounds',
  'invalid_body',
] as const;

export type SpecBillingErrorCode = (typeof SPEC_BILLING_ERROR_CODES)[number];

const KNOWN_BILLING_ERROR_CODES = new Set<string>(SPEC_BILLING_ERROR_CODES);

/** A known Spec billing rejection, so a page can branch on the code alone. */
export class SpecBillingError extends Error {
  readonly code: SpecBillingErrorCode;
  readonly status: number;
  /**
   * The whole response body, kept so a rejection that carries FACTS can hand
   * them on.
   *
   * Only `spec_assignment_stale` uses this today: its 409 travels with the
   * server's current assignment set, because a refusal that only says "stale"
   * would force the caller into a second read taken outside the lock that made
   * the answer true. Carried as the raw body rather than a parsed field so
   * this class stays one error type rather than one per code, and read through
   * `staleAssignmentSet` below rather than by picking at `message`.
   */
  readonly body: unknown;

  constructor(status: number, code: SpecBillingErrorCode, message: string, body?: unknown) {
    super(message);
    this.name = 'SpecBillingError';
    this.code = code;
    this.status = status;
    this.body = body;
  }
}

function toSpecBillingError(err: unknown): unknown {
  if (!(err instanceof ApiError) || !err.body || typeof err.body !== 'object') return err;
  const body = err.body as { error?: { code?: unknown; message?: unknown } };
  const code = body.error?.code;
  if (typeof code !== 'string' || !KNOWN_BILLING_ERROR_CODES.has(code)) return err;
  const message = body.error?.message;
  return new SpecBillingError(
    err.status,
    code as SpecBillingErrorCode,
    typeof message === 'string' ? message : err.message,
    err.body,
  );
}

/**
 * The server's CURRENT assignment set from a stale-write refusal, or null when
 * this is not one (or arrived without a readable set).
 *
 * The point of reading it here rather than re-fetching: the route computed it
 * under the workspace lock, in the same transaction that refused the write, so
 * it is the only answer guaranteed to be the one the write lost to. A caller
 * that re-read instead would be asking a question whose answer can have moved
 * again.
 *
 * Null on a malformed body rather than an empty array: an empty array is a
 * real answer ("nobody holds a seat") and must not be how a parse failure
 * looks.
 */
export function staleAssignmentSet(err: unknown): string[] | null {
  if (!(err instanceof SpecBillingError) || err.code !== 'spec_assignment_stale') return null;
  if (!err.body || typeof err.body !== 'object') return null;
  const assigned = (err.body as { assignedMemberIds?: unknown }).assignedMemberIds;
  if (!Array.isArray(assigned) || assigned.some((id) => typeof id !== 'string')) return null;
  return assigned as string[];
}

/**
 * Product copy for a rejection, in terms of what the manager can do next.
 *
 * The server's own messages describe the invariant that was violated ("Active
 * Spec seat assignments exceed the purchased or pending quantity"), which is
 * the right text for an API consumer and the wrong text for the person holding
 * the checkbox. Unknown failures keep their own message.
 */
export function specBillingErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof SpecBillingError) {
    switch (err.code) {
      case 'billing_forbidden':
        // Deliberately about BILLING rather than seats: the same code now
        // refuses a credit purchase, an auto-reload change and a spend-limit
        // change, and naming seats there would answer a question nobody asked.
        return 'Only workspace owners and admins can change Spec AI billing.';
      case 'spec_plan_ineligible':
        return 'AI seats are available on the Starter and Pro plans.';
      case 'spec_assignment_limit':
        return 'That is more assigned members than there are seats. Add seats or release one first.';
      case 'spec_assignment_stale':
        // Never "try again": the same request would be refused again for the
        // same reason, and a manager who retried it would be re-applying the
        // set the server already replaced. The only useful next step is to
        // work from the seats as they now stand.
        return 'Seat assignments changed while you were editing, so this change was not applied. The seats shown are the current ones.';
      case 'spec_member_inactive':
        return 'Everyone with a seat has to be an active member of this workspace. Reload the member list and try again.';
      case 'spec_payment_required':
        return 'Add a working payment method before changing Spec AI billing.';
      case 'spec_pricing_unavailable':
        return 'Spec pricing is unavailable right now. Try again in a few minutes.';
      case 'spec_read_only':
        return 'Spec is read-only right now, so billing changes are paused.';
      case 'spec_not_launched':
        return 'Spec is not available for this workspace.';
      case 'spec_credit_purchases_disabled':
        return 'Buying credits is turned off right now. Try again in a few minutes.';
      case 'spec_checkout_unavailable':
        return 'Checkout is unavailable right now. Nothing has been charged. Try again in a few minutes.';
      case 'spec_auto_reload_spend_limit_required':
        // ONE code, two directions: the auto-reload route refuses to turn
        // reload on without a limit, and the spend-limit route refuses to
        // clear a limit while reload is on. The sentence has to hold for both.
        return 'Auto-reload needs a monthly limit. Set a limit before turning auto-reload on, and turn auto-reload off before clearing the limit.';
      case 'invalid_amount':
        // Reachable while DISABLING, where the client-side amount guard does
        // not run: the stored amount is what a later enable would arm, so the
        // route bounds it either way. Names the bounds, because "invalid" on
        // its own leaves the person guessing which end they are on.
        return `The reload amount must be between ${formatSpecMoney(SPEC_CREDIT_PURCHASE_MIN_CENTS)} and ${formatSpecMoney(SPEC_CREDIT_PURCHASE_MAX_CENTS)}, in whole cents.`;
      case 'invalid_threshold':
        // Two ways in: a threshold of zero while enabling (a reload that can
        // never fire, shown as protection), and a threshold above the monthly
        // limit (a trigger outside the ceiling that governs it). The client
        // cannot mirror the second: the limit is a per-workspace row.
        return 'The reload threshold must be above zero and no higher than the monthly limit.';
      case 'invalid_limit':
        return 'Enter the monthly limit as a number of credits.';
      case 'limit_out_of_bounds':
        return `The monthly limit must be at most ${formatSpecCreditAmount(SPEC_SPEND_LIMIT_MAX_MICROS / SPEC_MICROS_PER_CREDIT)} credits.`;
      case 'invalid_body':
        // Not the user's mistake and not something they can fix by editing a
        // field, so this one says so rather than pointing at an input.
        return 'That change could not be saved because the request was malformed. Reload the page and try again.';
    }
  }
  return err instanceof Error && err.message ? err.message : fallback;
}

/**
 * The same copy, for a surface that may NEVER print a raw response body.
 *
 * `specBillingErrorMessage` falls through to `err.message`, which is right for
 * a caller that wants an unhandled error stated plainly. It is wrong on
 * Workspace Billing, because `ApiError.message` is built as
 * `API <status>: <body>` (lib/api.ts), so an unmapped 500 renders the server's
 * JSON on a live billing page.
 *
 * One definition rather than a copy per control: every Spec billing surface
 * must follow the same rule.
 */
export function specBillingSaveMessage(err: unknown, fallback: string): string {
  return err instanceof SpecBillingError ? specBillingErrorMessage(err, fallback) : fallback;
}

// ---------------------------------------------------------------------------
// Reads and writes
// ---------------------------------------------------------------------------

/** Readable by any workspace member. Keyed by workspace id, not slug. */
export async function getSpecBilling(workspaceId: string): Promise<SpecBillingSummary> {
  try {
    return await apiFetch<SpecBillingSummary>(`/workspaces/${workspaceId}/spec/billing`);
  } catch (err) {
    throw toSpecBillingError(err);
  }
}

/**
 * Readable by any workspace member. Keyed by workspace id, not slug, matching
 * `getSpecBilling` above.
 *
 * Deliberately does NOT parse the response with `SpecUsageBreakdownSchema`:
 * the route already validates against that exact schema before it responds
 * (spec-usage.ts), so re-parsing here would only reject the ENTIRE response if
 * a sixth category ever landed on the wire, which is precisely the failure
 * mode the server-side shaper exists to prevent. A `ZodError` would also reach
 * `specBillingSaveMessage` unconverted -- `toSpecBillingError` only recognizes
 * `ApiError` -- defeating the error wrapping every other read in this file
 * relies on.
 */
export async function getSpecUsageBreakdown(workspaceId: string): Promise<SpecUsageBreakdown> {
  try {
    return await apiFetch<SpecUsageBreakdown>(`/workspaces/${workspaceId}/spec/usage/breakdown`);
  } catch (err) {
    throw toSpecBillingError(err);
  }
}

/**
 * Readable by any workspace member. Keyed by workspace id, not slug, matching
 * `getSpecBilling` above.
 *
 * The ONE sibling read whose wire shape is not the bare summary: the route
 * answers `{workspaceId, memberId, usage}` (spec-usage.ts), because the same
 * envelope also carries the CALLING member's id, which nothing here needs.
 * Unwrapped to `SpecUsageSummary` here so this function's return type is
 * honest -- every caller wants the balance, never the envelope -- matching the
 * SHAPE `readSpecUsageEnvelope` in `lib/spec.ts` unwraps. NOT its presence
 * guard: that function throws a typed error when `usage` is absent, and this
 * one does not. Unguarded is fine today because the route always populates
 * `usage` from `readSpecMemberUsage`'s own schema-parsed result -- there is no
 * producer of this envelope that can omit the key -- but a reader relying on
 * that has no local defense if one ever does. Still no Zod parse of the inner
 * value, for the same reason as `getSpecUsageBreakdown`: the route already
 * validates it, and re-validating here would only add a second place this can
 * reject a shape the server already accepted.
 */
export async function getSpecUsageSummary(workspaceId: string): Promise<SpecUsageSummary> {
  try {
    const envelope = await apiFetch<{ usage: SpecUsageSummary }>(
      `/workspaces/${workspaceId}/spec/usage`,
    );
    return envelope.usage;
  } catch (err) {
    throw toSpecBillingError(err);
  }
}

/** Today's query_data rows against the plan's daily allowance (rolling window). */
export interface SpecQueryRows {
  used: number;
  allowance: number;
  windowHours: number;
  /** Whether Spec offers query_data in this workspace now; the line is only drawn when it does. */
  available: boolean;
}

export async function getSpecQueryRows(workspaceId: string): Promise<SpecQueryRows> {
  try {
    return await apiFetch<SpecQueryRows>(`/workspaces/${workspaceId}/spec/query-rows`);
  } catch (err) {
    throw toSpecBillingError(err);
  }
}

/** Today's web_search calls (searches plus page reads) against the plan's daily allowance. */
export interface SpecWebCalls {
  used: number;
  allowance: number;
  windowHours: number;
  /** Whether Spec offers web search in this workspace now; the line is only drawn when it does. */
  available: boolean;
}

export async function getSpecWebCalls(workspaceId: string): Promise<SpecWebCalls> {
  try {
    return await apiFetch<SpecWebCalls>(`/workspaces/${workspaceId}/spec/web-calls`);
  } catch (err) {
    throw toSpecBillingError(err);
  }
}

/** This period's enrichment credits (used of included) and pay-as-you-go spend by enrichment category. */
export async function getEnrichmentCredits(workspaceId: string): Promise<EnrichmentCreditsSummary> {
  try {
    return await apiFetch<EnrichmentCreditsSummary>(
      `/workspaces/${workspaceId}/spec/enrichment-credits`,
    );
  } catch (err) {
    throw toSpecBillingError(err);
  }
}

/**
 * Owner/admin only. An INCREASE takes effect immediately (and is prorated); a
 * DECREASE is queued for the next billing boundary. The route decides which
 * from the quantity it is given, which is why there is one `effective` literal
 * rather than a caller-chosen mode.
 */
export async function updateSpecSeatQuantity(
  workspaceId: string,
  input: { quantity: number; effective: SpecSeatEffective },
): Promise<SpecBillingSummary> {
  try {
    return await apiFetch<SpecBillingSummary>(
      `/workspaces/${workspaceId}/spec/billing/seat-quantity`,
      {
        method: 'PATCH',
        // Rebuilt rather than forwarded: a caller passing a wider object would
        // otherwise fail the route's exact-keys check with a 400.
        body: JSON.stringify({ quantity: input.quantity, effective: input.effective }),
      },
    );
  } catch (err) {
    throw toSpecBillingError(err);
  }
}

/**
 * Owner/admin only. Replaces the whole active assignment set: ids missing from
 * `memberIds` are released, so an empty array is a valid "release everything".
 * Every id must belong to an ACTIVE workspace member.
 *
 * `expectedMemberIds` is the set this change was BUILT FROM, and it is
 * required. The route compares it as a set under the workspace lock and
 * refuses with 409 `spec_assignment_stale` when it no longer matches, which is
 * what stops two managers editing at once from silently overwriting each
 * other: both payloads are individually legal, so nothing else can tell them
 * apart. Optional would be no fence at all, since every existing caller would
 * have gone on omitting it. On that 409, read the server's current set with
 * `staleAssignmentSet` and re-render from it rather than retrying.
 */
export async function updateSpecSeatAssignments(
  workspaceId: string,
  memberIds: readonly string[],
  expectedMemberIds: readonly string[],
): Promise<SpecBillingSummary> {
  try {
    return await apiFetch<SpecBillingSummary>(
      `/workspaces/${workspaceId}/spec/billing/seat-assignments`,
      {
        method: 'PATCH',
        body: JSON.stringify({
          memberIds: [...memberIds],
          expectedMemberIds: [...expectedMemberIds],
        }),
      },
    );
  } catch (err) {
    throw toSpecBillingError(err);
  }
}

/**
 * What the two credit-settings routes answer with.
 *
 * Deliberately NOT the full billing summary, unlike every seat write above:
 * these two routes echo the fields they wrote and nothing else
 * (spec-usage.ts). A caller that needs the rest of the balance -- what a
 * purchase is still pending, what expires when -- re-reads `getSpecBilling`
 * rather than assuming the parts of the summary these responses cannot speak
 * for.
 */
export interface SpecAutoReloadSettings {
  enabled: boolean;
  thresholdMicros: number;
  amountMicros: number;
}

export interface SpecSpendLimitSettings {
  /** Null is an explicitly CLEARED limit, which is unbounded, not zero. */
  limitMicros: number | null;
}

/**
 * Owner/admin only. Starts a Stripe Checkout session for a one-off credit
 * purchase and returns the URL to send the browser to.
 *
 * Nothing is granted here. The route records an intent and hands back a URL;
 * the payment intent's webhook is the only thing that credits the balance,
 * because the redirect return carries no proof of payment. A caller must
 * therefore never optimistically raise a displayed balance off this response.
 *
 * `amountCents` must be a safe integer between `SPEC_CREDIT_PURCHASE_MIN_CENTS`
 * and `SPEC_CREDIT_PURCHASE_MAX_CENTS`; the route 400s outside that range.
 */
export async function buySpecCredits(
  workspaceId: string,
  input: { amountCents: number },
): Promise<SpecCreditCheckoutResponse> {
  try {
    return await apiFetch<SpecCreditCheckoutResponse>(
      `/workspaces/${workspaceId}/spec/credits/checkout`,
      // Rebuilt key by key, like every write above: the route's body check is
      // exact, so one extra key is a 400.
      { method: 'POST', body: JSON.stringify({ amountCents: input.amountCents }) },
    );
  } catch (err) {
    throw toSpecBillingError(err);
  }
}

/**
 * Owner/admin only. Configures the off-session reload that funds a low
 * balance.
 *
 * The route refuses to ENABLE this without a positive spend limit already set
 * (409 `spec_auto_reload_spend_limit_required`): a reload with no ceiling is
 * an unbounded run of automatic charges, so the limit is the brake and the
 * server, not the dialog, is what enforces it.
 *
 * `amountMicros` must be whole cents expressed in microcredits, and strictly
 * positive when `enabled`.
 */
export async function updateSpecAutoReload(
  workspaceId: string,
  input: { enabled: boolean; thresholdMicros: number; amountMicros: number },
): Promise<SpecAutoReloadSettings> {
  try {
    return await apiFetch<SpecAutoReloadSettings>(
      `/workspaces/${workspaceId}/spec/credits/auto-reload`,
      {
        method: 'PATCH',
        body: JSON.stringify({
          enabled: input.enabled,
          thresholdMicros: input.thresholdMicros,
          amountMicros: input.amountMicros,
        }),
      },
    );
  } catch (err) {
    throw toSpecBillingError(err);
  }
}

/**
 * Owner/admin only. Caps how many credits this workspace may CONSUME in a
 * period, which is a different question from how many it may buy.
 *
 * `null` clears the limit, and is refused with a 409 while auto-reload is on.
 * A value above `SPEC_SPEND_LIMIT_MAX_MICROS` is a 400.
 */
export async function updateSpecSpendLimit(
  workspaceId: string,
  input: { limitMicros: number | null },
): Promise<SpecSpendLimitSettings> {
  try {
    return await apiFetch<SpecSpendLimitSettings>(
      `/workspaces/${workspaceId}/spec/credits/spend-limit`,
      { method: 'PATCH', body: JSON.stringify({ limitMicros: input.limitMicros }) },
    );
  } catch (err) {
    throw toSpecBillingError(err);
  }
}

// ---------------------------------------------------------------------------
// Draft parsing and the local pre-save preview
// ---------------------------------------------------------------------------

/**
 * A typed seat count, or null when the text is not one.
 *
 * Seats are a Stripe subscription quantity: fractional, negative, exponential
 * and out-of-range values are not requests the route can serve, so they are
 * caught here instead of being sent and coming back as a 400.
 */
export function parseSpecSeatQuantity(raw: string): number | null {
  const trimmed = raw.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const value = Number(trimmed);
  if (!Number.isSafeInteger(value) || value > MAX_SPEC_SEAT_QUANTITY) return null;
  return value;
}

export interface SpecSeatDraft {
  /** Purchased seat quantity the manager is asking for. */
  quantity: number;
  /** Members who should hold a seat once this saves. */
  memberIds: readonly string[];
}

export interface SpecSeatChangePreview {
  quantity: number;
  /** Measured against the PURCHASED quantity, which is what the route compares. */
  direction: 'unchanged' | 'add' | 'reduce';
  /** True when saving this draft would write the seat quantity at all. */
  quantityChanged: boolean;
  /** True when the assignment set differs, ignoring order. */
  assignmentsChanged: boolean;
  /** Purchased seats the workspace holds the moment the save lands. */
  purchasedAfterSave: number;
  /** Purchased seats once the change has fully taken effect. */
  purchasedWhenEffective: number;
  /** ISO instant a reduction lands on; null when the change is immediate. */
  effectiveAt: string | null;
  assignedSeatCount: number;
  /** Most assignments the route will accept for this draft. */
  assignmentCapacity: number;
  exceedsCapacity: boolean;
  /** Whether the quantity write has to go first for the draft to be legal. */
  savesQuantityFirst: boolean;
  /** Recurring price of the steady state this draft describes. */
  monthlySubtotalCents: number;
  /** Pooled workspace credits per month in the same steady state. */
  pooledCreditsMicros: number;
}

function sameMembership(left: readonly string[], right: readonly string[]): boolean {
  if (left.length !== right.length) return false;
  const seen = new Set(right);
  return left.every((id) => seen.has(id));
}

/**
 * What saving this draft would do, derived entirely from the summary the page
 * already holds.
 *
 * Deliberately NOT a round trip. The base-plan seat dialog previews through
 * Stripe because it quotes an exact proration; this control quotes the
 * recurring subtotal and the pooled grant, both of which are `seatPriceCents`
 * and `includedCreditsPerSeatMicros` times a quantity. A preview endpoint here
 * would add a Stripe dependency to a page that does not need one.
 *
 * The two ordering/capacity facts mirror gates in
 * apps/api/src/routes/spec-usage.ts:
 *
 *  - assignments are gated on `pendingSeatQuantity ?? seatQuantity` (line 921),
 *    so a queued reduction lowers the ceiling even while more seats are still
 *    purchased;
 *  - a quantity write is refused below the active assignment count (line 837).
 *
 * A draft that raises the ceiling therefore has to write the quantity FIRST,
 * and every other draft has to write the assignments first. Getting that order
 * wrong turns a legal change into a 409.
 */
export function previewSpecSeatChange(
  summary: SpecBillingSummary,
  draft: SpecSeatDraft,
): SpecSeatChangePreview {
  const purchased = summary.purchasedSeatQuantity;
  // The quantity the route currently gates assignments on.
  const currentCeiling = summary.pendingSeatQuantity ?? purchased;
  const direction =
    draft.quantity > purchased ? 'add' : draft.quantity < purchased ? 'reduce' : 'unchanged';
  const savesQuantityFirst = draft.quantity > currentCeiling;
  const assignmentCapacity = savesQuantityFirst
    ? draft.quantity
    : Math.min(draft.quantity, currentCeiling);

  return {
    quantity: draft.quantity,
    direction,
    // Compared against the ceiling, not the purchased count: re-submitting a
    // reduction that is already queued writes nothing, while restoring the old
    // quantity genuinely cancels it.
    quantityChanged: draft.quantity !== currentCeiling,
    assignmentsChanged: !sameMembership(draft.memberIds, summary.assignedMemberIds),
    purchasedAfterSave: direction === 'add' ? draft.quantity : purchased,
    purchasedWhenEffective: draft.quantity,
    effectiveAt: direction === 'reduce' ? summary.period.endsAt : null,
    assignedSeatCount: draft.memberIds.length,
    assignmentCapacity,
    exceedsCapacity: draft.memberIds.length > assignmentCapacity,
    savesQuantityFirst,
    monthlySubtotalCents: draft.quantity * summary.seatPriceCents,
    pooledCreditsMicros: draft.quantity * summary.includedCreditsPerSeatMicros,
  };
}

// ---------------------------------------------------------------------------
// Display
// ---------------------------------------------------------------------------

/**
 * Credits from microcredits, or null when the unit is unusable.
 *
 * `microsPerCredit` is the fixed microcredits-per-credit unit (pinned to
 * 1_000_000), not a price. A zero or negative unit would
 * otherwise render "Infinity credits per month" on a billing page, so there is
 * no number to show rather than a wrong one.
 */
export function specCreditsFromMicros(micros: number, microsPerCredit: number): number | null {
  if (!Number.isFinite(microsPerCredit) || microsPerCredit <= 0) return null;
  return micros / microsPerCredit;
}

/** Rounds a credit amount for a human. Usage display may round; requests never do. */
export function formatSpecCreditAmount(credits: number): string {
  return credits.toLocaleString('en-US', {
    maximumFractionDigits: CREDIT_DISPLAY_MAX_FRACTION_DIGITS,
  });
}

/**
 * A money figure in cents. Whole dollars lose the empty cents, so a $20 seat
 * price reads "$20 per assigned member/month" rather than "$20.00 per assigned
 * member/month"; a configured price with cents still renders in full.
 *
 * USD is ASSUMED, not read: `SpecBillingSummary` carries no currency, unlike the
 * base-plan seat preview (`SeatChangePreview.currency`), which does. The
 * consequence is real and worth naming: a workspace billed in another currency
 * would see its base plan in that currency and the Spec seat price in dollars on
 * the same page. Fixing it means adding a currency to the summary and the
 * pricing version, not defaulting one here.
 */
export function formatSpecMoney(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

const BILLING_DATE_FORMAT = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  // Pinned to UTC, like the rest of Parcel's billing-boundary display: the API
  // serializes these instants in UTC and Stripe dates the invoice the same way,
  // so a reader west of Greenwich must not be told the period ends a day
  // earlier than their invoice says it does.
  timeZone: 'UTC',
});

/** A billing boundary for display, or null when there is no date to show. */
export function formatSpecBillingDate(iso: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return BILLING_DATE_FORMAT.format(date);
}
